import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { showToast } from "../toastService";
import { apiFetch } from "../utils/api";
import Header from "../components/Header";
import BottomNavbar from "../components/BottomNavbar";
import LocationPicker from "../components/LocationPicker";
import "./css/Profile.css";

const USER_BASE_URL = process.env.REACT_APP_USER_BASE_URL;
const VERSION = process.env.REACT_APP_VERSION;
const UAE_STATES = [
  "Abu Dhabi",
  "Dubai",
  "Sharjah",
  "Ajman",
  "Umm Al Quwain",
  "Ras Al Khaimah",
  "Fujairah",
];

export default function Profile() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState(null);
  const [lang, setLang] = useState(localStorage.getItem("app_language") || "en");
  const [showLocationPicker, setShowLocationPicker] = useState(false);
  const fileInputRef = useRef(null);

  const [name, setName] = useState("");
  const [whatsapp, setWhatsapp] = useState("");
  const [email, setEmail] = useState("");
  const [address1, setAddress1] = useState("");
  const [address2, setAddress2] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [pincode, setPincode] = useState("");
  const [latitude, setLatitude] = useState("");
  const [longitude, setLongitude] = useState("");
  const [profileImageFile, setProfileImageFile] = useState(null);
  const [profileImagePreview, setProfileImagePreview] = useState(null);
  const [touched, setTouched] = useState({});
  const [throwError, setThrowError] = useState(false);

  const t = {
    en: {
      generalSettings: "General Settings",
      profileSection: "Profile",
      profileCompletionWarning:
        "Please add address details to mark this section as completed!",
      profilePhoto: "Profile Photo",
      nameLabel: "Name",
      namePlaceholder: "Enter your name",
      nameError: "Please enter a valid name.",
      whatsappLabel: "Whatsapp Number",
      whatsappPlaceholder: "50 XXX XXXX",
      whatsappError: "Please enter a valid WhatsApp number.",
      emailLabel: "Email",
      emailPlaceholder: "Enter email address",
      emailError: "Please enter a valid email address.",
      address1Label: "Address Line 1",
      address1Placeholder: "Enter address line 1",
      address1Error: "Address line 1 is required.",
      address2Label: "Address Line 2",
      address2Placeholder: "Enter address line 2 (optional)",
      address2Error: "Please enter a valid address line 2.",
      cityLabel: "City",
      cityPlaceholder: "Enter city",
      cityError: "Please enter a valid city.",
      emirateLabel: "Emirate",
      selectEmirate: "Select Emirate",
      emirateError: "Please select an Emirate.",
      poBoxLabel: "PO Box",
      poBoxPlaceholder: "Enter PO Box",
      poBoxError: "Please enter a valid PO Box.",
      locationLabel: "Location On Map",
      locationButton: "Click Here to Select",
      locationSelected: "Location Selected",
      locationRequired: "Please select your location on map.",
      latitudeLabel: "Latitude",
      longitudeLabel: "Longitude",
      removeLocation: "Remove location",
      save: "Save",
      saving: "Saving...",
      saveSuccess: "Profile updated successfully",
      apiError: "Something went wrong!",
      readOnlyWarning:
        "The data given below is read only, if you want to update any details given below",
      contactUs: "Contact Us",
      phoneNumber: "Phone Number",
      loading: "Loading",
      invalidCoordinates: "Invalid coordinates selected.",
    },
    ar: {
      generalSettings: "الإعدادات العامة",
      profileSection: "الملف الشخصي",
      profileCompletionWarning:
        "يرجى إضافة تفاصيل العنوان لوضع علامة على هذا القسم كمكتمل!",
      profilePhoto: "صورة الملف الشخصي",
      nameLabel: "الاسم",
      namePlaceholder: "أدخل اسمك",
      nameError: "يرجى إدخال اسم صحيح.",
      whatsappLabel: "رقم الواتساب",
      whatsappPlaceholder: "50 XXX XXXX",
      whatsappError: "يرجى إدخال رقم واتساب صحيح.",
      emailLabel: "البريد الإلكتروني",
      emailPlaceholder: "أدخل البريد الإلكتروني",
      emailError: "يرجى إدخال بريد إلكتروني صحيح.",
      address1Label: "عنوان السطر 1",
      address1Placeholder: "أدخل عنوان السطر 1",
      address1Error: "عنوان السطر 1 مطلوب.",
      address2Label: "عنوان السطر 2",
      address2Placeholder: "أدخل عنوان السطر 2 (اختياري)",
      address2Error: "يرجى إدخال عنوان سطر 2 صحيح.",
      cityLabel: "المدينة",
      cityPlaceholder: "أدخل المدينة",
      cityError: "يرجى إدخال مدينة صحيحة.",
      emirateLabel: "الإمارة",
      selectEmirate: "اختر الإمارة",
      emirateError: "يرجى اختيار الإمارة.",
      poBoxLabel: "صندوق البريد",
      poBoxPlaceholder: "أدخل صندوق البريد",
      poBoxError: "يرجى إدخال صندوق بريد صالح.",
      locationLabel: "الموقع على الخريطة",
      locationButton: "انقر هنا للاختيار",
      locationSelected: "تم اختيار الموقع",
      locationRequired: "يرجى اختيار موقعك على الخريطة.",
      latitudeLabel: "خط العرض",
      longitudeLabel: "خط الطول",
      removeLocation: "إزالة الموقع",
      save: "حفظ",
      saving: "جاري الحفظ...",
      saveSuccess: "تم تحديث الملف الشخصي بنجاح",
      apiError: "حدث خطأ ما!",
      readOnlyWarning:
        "البيانات المذكورة أدناه للقراءة فقط، إذا كنت تريد تحديث أي تفاصيل أدناه",
      contactUs: "اتصل بنا",
      phoneNumber: "رقم الهاتف",
      loading: "جاري التحميل",
      invalidCoordinates: "تم اختيار إحداثيات غير صالحة.",
    },
  }[lang];

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        setLoading(true);
        const data = await apiFetch(
          `${USER_BASE_URL}/${VERSION}/profile/`,
          "GET",
          null,
          {}
        );

        if (data && data.response_code === 200 && data.response) {
          const profileData = data.response;

          if (profileData.language_preference) {
            localStorage.setItem("app_language", profileData.language_preference);
            setLang(profileData.language_preference);
          }

          setProfile(profileData);

          setName(profileData.name || "");
          setEmail(profileData.email || "");
          setAddress1(profileData.address1 || "");
          setAddress2(profileData.address2 || "");
          setCity(profileData.city || "");
          setState(profileData.state || "");
          setPincode(
            profileData.pincode !== undefined && profileData.pincode !== null
              ? String(profileData.pincode)
              : ""
          );

          if (profileData.whatsapp_number) {
            const cleaned = profileData.whatsapp_number
              .toString()
              .replace(/^\+?971/, "");
            setWhatsapp(cleaned);
          }

          if (
            Array.isArray(profileData.location_coordinates) &&
            profileData.location_coordinates.length === 2
          ) {
            // DB format: [longitude, latitude]
            const [lng, lat] = profileData.location_coordinates;
            setLatitude(String(lat));
            setLongitude(String(lng));
          }

          if (profileData.profile_image?.url) {
            setProfileImagePreview(profileData.profile_image.url);
          }
        }
      } catch (err) {
        console.error("Error fetching profile:", err);
        showToast(err.message || t.apiError, "error");
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePhotoChange = (event) => {
    event.stopPropagation();
    const file = event.target.files?.[0];
    if (!file) return;

    const validTypes = [
      "image/png",
      "image/jpg",
      "image/jpeg",
      "image/svg+xml",
      "image/webp",
      "image/avif",
      "image/heic",
      "image/heif",
    ];

    if (!validTypes.includes(file.type)) {
      showToast(
        lang === "ar"
          ? "يجب أن يكون تنسيق الملف PNG أو JPG أو JPEG أو SVG أو WebP أو AVIF أو HEIC"
          : "File format should be PNG, JPG, JPEG, SVG, WebP, AVIF, or HEIC",
        "error"
      );
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }

    const maxSize = 2 * 1024 * 1024;
    if (file.size > maxSize) {
      showToast(
        lang === "ar"
          ? "يجب أن يكون حجم الملف أقل من 2 ميجابايت"
          : "File size should be less than 2MB",
        "error"
      );
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }

    setProfileImageFile(file);

    const reader = new FileReader();
    reader.onloadend = () => {
      setProfileImagePreview(reader.result);
      if (fileInputRef.current) fileInputRef.current.value = "";
    };
    reader.onerror = () => {
      if (fileInputRef.current) fileInputRef.current.value = "";
    };
    reader.readAsDataURL(file);
  };

  const validateName = (value) => {
    const trimmed = value.trim();
    return trimmed.length >= 1 && trimmed.length <= 100;
  };

  const validateWhatsapp = (value) => {
    const trimmed = value.trim();
    if (trimmed === "") return true;
    if (!/^[0-9]{9}$/.test(trimmed)) return false;
    return /^(50|52|54|55|56|58)/.test(trimmed);
  };

  const validateEmail = (value) =>
    /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(value.trim()) &&
    value.trim().length >= 5 &&
    value.trim().length <= 255;

  const validateAddress1 = (value) => {
    const trimmed = value.trim();
    return trimmed.length >= 1 && trimmed.length <= 255;
  };

  const validateAddress2 = (value) => {
    const trimmed = value.trim();
    return trimmed.length === 0 || trimmed.length <= 255;
  };

  const validateCity = (value) => {
    const trimmed = value.trim();
    return trimmed.length >= 1 && trimmed.length <= 100;
  };

  const validateState = (value) => UAE_STATES.includes(value);

  const validatePincode = (value) => {
    if (!value.trim()) return false;
    const numeric = Number(value);
    return !Number.isNaN(numeric) && numeric > 0;
  };

  const validateLocation = () => {
    if (!latitude || !longitude) return false;
    const latNum = Number(latitude);
    const lngNum = Number(longitude);
    return !Number.isNaN(latNum) && !Number.isNaN(lngNum);
  };

  const handleLocationSelect = (lat, lng) => {
    if (Number.isFinite(lat) && Number.isFinite(lng)) {
      setLatitude(lat.toFixed(6));
      setLongitude(lng.toFixed(6));
      setTouched((prev) => ({ ...prev, latitude: true, longitude: true }));
    } else {
      showToast(t.invalidCoordinates, "error");
    }
  };

  const handleRemoveLocation = () => {
    setLatitude("");
    setLongitude("");
  };

  const handleSave = async () => {
    setThrowError(false);

    const isValid =
      validateName(name) &&
      validateWhatsapp(whatsapp) &&
      validateEmail(email) &&
      validateAddress1(address1) &&
      validateAddress2(address2) &&
      validateCity(city) &&
      validateState(state) &&
      validatePincode(pincode) &&
      validateLocation();

    if (!isValid) {
      setThrowError(true);
      if (!validateLocation()) {
        showToast(t.locationRequired, "error");
      }
      return;
    }

    const payload = {
      name: name.trim(),
      email: email.trim(),
      address1: address1.trim(),
      city: city.trim(),
      pincode: Number(pincode),
      state,
    };

    if (whatsapp.trim()) {
      payload.whatsapp_number = `971${whatsapp.trim()}`;
    }

    if (address2.trim()) {
      payload.address2 = address2.trim();
    }

    if (validateLocation()) {
      // DB format: [longitude, latitude]
      payload.location_coordinates = [
        Number(Number(longitude).toFixed(6)),
        Number(Number(latitude).toFixed(6)),
      ];
    }

    const formData = new FormData();
    Object.entries(payload).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        formData.append(key, JSON.stringify(value));
      } else {
        formData.append(key, value);
      }
    });

    if (profileImageFile) {
      formData.append("file", profileImageFile);
    }

    setSaving(true);

    try {
      const data = await apiFetch(
        `${USER_BASE_URL}/${VERSION}/profile/edit`,
        "POST",
        formData,
        {}
      );

      if (data && data.response_code === 200) {
        showToast(data.message || t.saveSuccess, "success");
        localStorage.setItem("username", payload.name);
        setProfile((prev) => ({
          ...(prev || {}),
          ...payload,
          whatsapp_number: payload.whatsapp_number || "",
          location_coordinates: payload.location_coordinates || [],
        }));
        setProfileImageFile(null);
      } else {
        showToast(data?.message || t.apiError, "error");
      }
    } catch (err) {
      console.error("Error updating profile:", err);
      showToast(err.message || t.apiError, "error");
    } finally {
      setSaving(false);
    }
  };

  const formatPhoneNumber = (phone) => {
    if (!phone) return "";
    if (phone.startsWith("971")) {
      return phone.substring(3);
    }
    return phone;
  };

  const isProfileWarningVisible =
    !profile?.address1 || !profile?.city || !profile?.state || !profile?.pincode;

  const locationSelected = Boolean(latitude && longitude);

  if (loading) {
    return (
      <div className="profile-loading">
        <div className="spinner"></div>
        <p>{t.loading}</p>
      </div>
    );
  }

  if (!profile) {
    return null;
  }

  return (
    <div className="profile-container" dir={lang === "ar" ? "rtl" : "ltr"}>
      <Header lang={lang} />

      <div className="profile-page-header">
        <button className="back-button" onClick={() => navigate(-1)}>
          <i className="bx bx-arrow-back"></i>
        </button>
        <h1 className="profile-page-title">{t.generalSettings}</h1>
        <div className="header-spacer"></div>
      </div>

      <div className="profile-content">
        {isProfileWarningVisible && (
          <div className="profile-warning-banner">
            <p>{t.profileCompletionWarning}</p>
          </div>
        )}

        <div className="profile-section">
          <h2 className="profile-section-title">{t.profileSection}</h2>
        </div>

        <div className="profile-section">
          <label className="profile-label">{t.profilePhoto}</label>
          <div className="logo-upload-container">
            <div
              className="logo-preview-wrapper"
              onClick={(e) => {
                e.stopPropagation();
                if (!saving && fileInputRef.current) {
                  fileInputRef.current.click();
                }
              }}
            >
              <div className="logo-preview">
                {profileImagePreview ? (
                  <img src={profileImagePreview} alt="Profile" />
                ) : (
                  <div className="logo-placeholder">
                    <i className="bx bx-image"></i>
                  </div>
                )}
              </div>
              <div className="logo-upload-btn">
                <i className="bx bx-camera"></i>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpg,image/jpeg,image/svg+xml,image/webp,image/avif,image/heic,image/heif"
                onChange={handlePhotoChange}
                disabled={saving}
                style={{ display: "none" }}
              />
            </div>
          </div>
        </div>

        <form className="profile-form" onSubmit={(e) => e.preventDefault()}>
          <div className="profile-form-group">
            <label className="profile-label">{t.nameLabel}</label>
            <input
              className="profile-input"
              type="text"
              placeholder={t.namePlaceholder}
              maxLength={100}
              value={name}
              disabled={saving}
              onChange={(e) => {
                setName(e.target.value);
                setTouched((prev) => ({ ...prev, name: true }));
              }}
            />
            {(touched.name || throwError) && !validateName(name) && (
              <div className="profile-error">{t.nameError}</div>
            )}
          </div>

          <div className="profile-form-group">
            <label className="profile-label">{t.whatsappLabel}</label>
            <div
              className={`profile-input phone-input ${
                (touched.whatsapp || throwError) && !validateWhatsapp(whatsapp)
                  ? "error"
                  : ""
              }`}
            >
              <span className="flag">🇦🇪</span>
              <span className="country-code">+971</span>
              <input
                type="text"
                inputMode="numeric"
                maxLength={9}
                placeholder={t.whatsappPlaceholder}
                value={whatsapp}
                disabled={saving}
                onChange={(e) => {
                  const digits = e.target.value.replace(/\D/g, "");
                  if (digits.length <= 9) {
                    setWhatsapp(digits);
                    setTouched((prev) => ({ ...prev, whatsapp: true }));
                  }
                }}
              />
            </div>
            {(touched.whatsapp || throwError) && !validateWhatsapp(whatsapp) && (
              <div className="profile-error">{t.whatsappError}</div>
            )}
          </div>

          <div className="profile-form-group">
            <label className="profile-label">{t.emailLabel}</label>
            <input
              className="profile-input"
              type="email"
              placeholder={t.emailPlaceholder}
              maxLength={255}
              value={email}
              disabled={saving}
              onChange={(e) => {
                setEmail(e.target.value);
                setTouched((prev) => ({ ...prev, email: true }));
              }}
            />
            {(touched.email || throwError) && !validateEmail(email) && (
              <div className="profile-error">{t.emailError}</div>
            )}
          </div>

          <div className="profile-form-group">
            <label className="profile-label">{t.address1Label}</label>
            <input
              className="profile-input"
              type="text"
              placeholder={t.address1Placeholder}
              maxLength={255}
              value={address1}
              disabled={saving}
              onChange={(e) => {
                setAddress1(e.target.value);
                setTouched((prev) => ({ ...prev, address1: true }));
              }}
            />
            {(touched.address1 || throwError) && !validateAddress1(address1) && (
              <div className="profile-error">{t.address1Error}</div>
            )}
          </div>

          <div className="profile-form-group">
            <label className="profile-label">{t.address2Label}</label>
            <input
              className="profile-input"
              type="text"
              placeholder={t.address2Placeholder}
              maxLength={255}
              value={address2}
              disabled={saving}
              onChange={(e) => {
                setAddress2(e.target.value);
                setTouched((prev) => ({ ...prev, address2: true }));
              }}
            />
            {(touched.address2 || throwError) && !validateAddress2(address2) && (
              <div className="profile-error">{t.address2Error}</div>
            )}
          </div>

          <div className="profile-form-group">
            <label className="profile-label">{t.cityLabel}</label>
            <input
              className="profile-input"
              type="text"
              placeholder={t.cityPlaceholder}
              maxLength={100}
              value={city}
              disabled={saving}
              onChange={(e) => {
                setCity(e.target.value);
                setTouched((prev) => ({ ...prev, city: true }));
              }}
            />
            {(touched.city || throwError) && !validateCity(city) && (
              <div className="profile-error">{t.cityError}</div>
            )}
          </div>

          <div className="profile-form-group">
            <label className="profile-label">{t.emirateLabel}</label>
            <select
              className="profile-select"
              value={state}
              disabled={saving}
              onChange={(e) => {
                setState(e.target.value);
                setTouched((prev) => ({ ...prev, state: true }));
              }}
            >
              <option value="">{t.selectEmirate}</option>
              {UAE_STATES.map((item) => (
                <option key={item} value={item}>
                  {item}
                </option>
              ))}
            </select>
            {(touched.state || throwError) && !validateState(state) && (
              <div className="profile-error">{t.emirateError}</div>
            )}
          </div>

          <div className="profile-form-group">
            <label className="profile-label">{t.poBoxLabel}</label>
            <input
              className="profile-input"
              type="text"
              inputMode="numeric"
              placeholder={t.poBoxPlaceholder}
              value={pincode}
              disabled={saving}
              onChange={(e) => {
                const digits = e.target.value.replace(/\D/g, "");
                setPincode(digits);
                setTouched((prev) => ({ ...prev, pincode: true }));
              }}
            />
            {(touched.pincode || throwError) && !validatePincode(pincode) && (
              <div className="profile-error">{t.poBoxError}</div>
            )}
          </div>

          <div className="profile-form-group">
            <label className="profile-label">{t.locationLabel}</label>
            <button
              type="button"
              className={`location-select-btn ${
                locationSelected ? "selected" : ""
              }`}
              onClick={() => setShowLocationPicker(true)}
              disabled={saving}
            >
              {locationSelected ? t.locationSelected : t.locationButton}
            </button>
            {locationSelected && (
              <div className="location-selected-details">
                <span>
                  {t.latitudeLabel}: {latitude}
                </span>
                <span>
                  {t.longitudeLabel}: {longitude}
                </span>
                <button
                  type="button"
                  className="remove-location-btn"
                  onClick={handleRemoveLocation}
                  disabled={saving}
                >
                  {t.removeLocation}
                </button>
              </div>
            )}
            {(touched.latitude || touched.longitude || throwError) &&
              !validateLocation() && (
                <div className="profile-error">{t.locationRequired}</div>
              )}
          </div>
        </form>

        <button
          className="profile-save-btn"
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? (
            <>
              <span className="save-spinner"></span>
              {t.saving}
            </>
          ) : (
            t.save
          )}
        </button>

        <div className="profile-readonly-warning">
          <p>
            {t.readOnlyWarning}
            <br />
            <a
              href="mailto:info@fixora-app.com"
              className="contact-us-link"
              onClick={(e) => {
                e.preventDefault();
                window.location.href = "mailto:info@fixora-app.com";
              }}
            >
              {t.contactUs}
            </a>
          </p>
        </div>

        <div className="profile-readonly-section">
          <div className="profile-field">
            <label className="profile-field-label">{t.phoneNumber}</label>
            <div className="profile-field-value disabled">
              <span className="flag">🇦🇪</span>
              <span>+971</span>
              <span>{formatPhoneNumber(profile.phone || "")}</span>
            </div>
          </div>
        </div>
      </div>

      <BottomNavbar currentPage="settings" lang={lang} />

      <LocationPicker
        isOpen={showLocationPicker}
        onClose={() => setShowLocationPicker(false)}
        onSelect={handleLocationSelect}
        initialLat={latitude ? Number(latitude) : null}
        initialLng={longitude ? Number(longitude) : null}
        lang={lang}
      />
    </div>
  );
}

